import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NwNumber } from '../../../../shared/models/nw-number/nw-number';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TrackUnsavedFieldBase } from '../../base/track-unsaved-field-base';
import { FloppyIconComponent } from '../../icons/floppy-icon/floppy-icon.component';
import { STORAGE_KEYS } from '../../../../shared/constants/storage-keys';
import { TextAreaComponent } from '../../common/text-area/text-area.component';
import { LabelWithHelp } from '../../common/label-with-help/label-with-help';
import { SelectionComponent } from '../../common/selection/selection.component';
import { ProjectApiService } from '../../../services/api/project-api.service';
import { SelectionOptionValue } from '../../../../shared/models/internal/selection-option-value';
import { TrackUnsavedFieldDirective } from '../../../directives/track-unsaved-field.directive';
import { AgentActionDirective } from '../../../directives/agent-action.directive';
import { NwNumberApiService } from '../../../services/api/nw-number-api.service';
import { AppInsightsService } from '../../../services/app-insights.service';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-edit-nw-number',
  imports: [
    ReactiveFormsModule,
    FloppyIconComponent,
    TextAreaComponent,
    LabelWithHelp,
    SelectionComponent,
    TrackUnsavedFieldDirective,
    AgentActionDirective,
  ],
  templateUrl: './edit-nw-number.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-nw-number.component.scss',
})
export class EditNwNumber extends TrackUnsavedFieldBase<NwNumber> implements OnInit, OnChanges {
  @Input() public nwNumber?: NwNumber;
  @Output() public updatedNwNumber = new EventEmitter<NwNumber>();

  protected override formGroup: FormGroup<{
    internalNotes: FormControl<string | null>;
    shortDescription: FormControl<string | null>;
    productLine: FormControl<string | null>;
    projectId: FormControl<string | null>;
  }>;
  protected projectOptions: SelectionOptionValue[] = [];
  protected hasProjectIdUnsavedChanges = false;

  private readonly nwNumberApi = inject(NwNumberApiService);
  private readonly projectApi = inject(ProjectApiService);
  private readonly toast = inject(HotToastService);
  private appInsightsService = inject(AppInsightsService);

  constructor() {
    super(STORAGE_KEYS.UNSAVED_NW_NUMBER);
    this.formGroup = new FormGroup({
      internalNotes: new FormControl<string | null>(null),
      shortDescription: new FormControl<string | null>(null),
      productLine: new FormControl<string | null>(null),
      projectId: new FormControl<string | null>(null),
    });
  }

  public ngOnInit(): void {
    this.appInsightsService.logSidebarUsage('NwNumber_edit');
    this.fetchProjectOptions();
    this.formGroup.patchValue(this.nwNumber ?? {});
  }

  public ngOnChanges(): void {
    this.resetFieldTracking$.next(undefined);
    this.previousItemId = this.item?.id;
  }

  protected override getCurrentItemFromFormValues(): NwNumber {
    const rawValues = this.formGroup.getRawValue();
    const currentNwNumber: NwNumber = {
      ...this.nwNumber,
      internalNotes: rawValues.internalNotes ?? undefined,
      shortDescription: rawValues.shortDescription ?? undefined,
      productLine: rawValues.productLine ?? undefined,
      projectId: rawValues.projectId ?? undefined,
    };
    return currentNwNumber;
  }

  protected updateNwNumber(): void {
    if (this.nwNumber && this.nwNumber.id) {
      const updatedNwNumber = this.getCurrentItemFromFormValues();
      this.nwNumberApi.updateNwNumber(this.nwNumber.id, updatedNwNumber).subscribe({
        next: (response) => {
          this.updatedNwNumber.emit(response);
          this.formGroup.markAsPristine();
          this.toast.success('NW-Number updated successfully');
          this.resetFieldTracking$.next(undefined);
        },
        error: (error) => {
          console.error('Error updating NW-Number:', error);
        },
      });
    }
  }

  private fetchProjectOptions(): void {
    this.projectApi.getProjects().subscribe({
      next: (projects) => {
        this.projectOptions = [
          ...projects.map((project) => ({
            id: project.id ?? '',
            title: project.name ?? '<Unnamed Project>',
            selected: this.nwNumber?.projectId === project.id,
          })),
        ];
      },
      error: (error) => {
        console.error('Error fetching projects:', error);
      },
    });
  }
}
